import { Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import type { FormikErrors, FormikTouched } from 'formik';
import AiSettingsIcon from '@/app/common/components/AiSettingsIcon';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { keywordResearchTooltips } from '@/app/KeywordResearch/tooltip/keywordResearchTooltips';
import MassiveKeywordLinesInput, {
  OperatorToggle
} from './MassiveKeywordLinesInput';
import { parseKeywords, type MassiveFormValues } from './massiveFormConfig';

type MassiveFormFiltersSectionProps = {
  values: MassiveFormValues;
  errors: FormikErrors<MassiveFormValues>;
  touched: FormikTouched<MassiveFormValues>;
  setFieldValue: (field: string, value: unknown) => void;
  handleBlur: (e: React.FocusEvent) => void;
};

type FilterOptionCardProps = {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  active?: boolean;
  variant: 'smart' | 'results';
  tooltip: React.ReactNode;
  onChange: () => void;
  badge?: React.ReactNode;
};

const FilterOptionCard = ({
  id,
  label,
  checked,
  disabled = false,
  active = false,
  variant,
  tooltip,
  onChange,
  badge
}: FilterOptionCardProps) => {
  const isSmart = variant === 'smart';
  const aiAccent = '#a855f7';

  return (
    <label
      className={classNames(
        'd-flex flex-row-reverse align-items-center gap-2 w-100 m-0 py-2 px-2 rounded-3 user-select-none',
        disabled ? 'opacity-50' : 'cursor-pointer',
        {
          'border border-primary': active && !isSmart,
          border: !active
        }
      )}
      style={
        active && isSmart
          ? {
              border: `1px solid ${aiAccent}`,
              backgroundColor: 'rgba(168, 85, 247, 0.06)',
              boxShadow: '0 0 0 1px rgba(168, 85, 247, 0.12)'
            }
          : active && !isSmart
            ? {
                backgroundColor: 'rgba(var(--falcon-primary-rgb), 0.08)',
                boxShadow: '0 0 0 1px rgba(var(--falcon-primary-rgb), 0.12)'
              }
            : undefined
      }
    >
      <input
        type="checkbox"
        id={id}
        className={classNames(
          'form-check-input flex-shrink-0 m-0',
          !disabled && 'cursor-pointer'
        )}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        style={
          checked && isSmart
            ? {
                backgroundColor: aiAccent,
                borderColor: aiAccent
              }
            : checked && !isSmart
              ? {
                  backgroundColor: 'var(--falcon-primary)',
                  borderColor: 'var(--falcon-primary)'
                }
              : undefined
        }
      />
      <span className="flex-grow-1 d-inline-flex align-items-center gap-1 min-w-0">
        <span className="fs--1 text-nowrap d-inline-flex align-items-center gap-1 lh-sm">
          {label}
          {badge}
        </span>
        <HelpTooltip
          className="ms-0 lh-1 flex-shrink-0"
          icon="info-circle"
          html={tooltip}
        />
      </span>
      <span
        className={classNames(
          'd-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0',
          !isSmart && 'text-primary'
        )}
        style={{
          width: '2rem',
          height: '2rem',
          ...(isSmart
            ? {
                backgroundColor: 'rgba(168, 85, 247, 0.14)',
                color: aiAccent
              }
            : {
                backgroundColor: 'rgba(var(--falcon-primary-rgb), 0.14)'
              })
        }}
      >
        {isSmart ? (
          <AiSettingsIcon size={16} />
        ) : (
          <FontAwesomeIcon icon="filter" className="fs--1" />
        )}
      </span>
    </label>
  );
};

const MassiveFormFiltersSection = ({
  values,
  errors,
  touched,
  setFieldValue,
  handleBlur
}: MassiveFormFiltersSectionProps) => {
  const includeCount = parseKeywords(values.include_keywords).length;
  const excludeCount = parseKeywords(values.exclude_keywords).length;
  const hasActiveFilters = includeCount > 0 || excludeCount > 0;

  const handleSmartFilterChange = () => {
    const next = !values.smart_filter;
    setFieldValue('smart_filter', next);
    if (next) {
      setFieldValue('filter_results', false);
    }
  };

  const handleFilterResultsChange = () => {
    const next = !values.filter_results;
    setFieldValue('filter_results', next);
    if (next) {
      setFieldValue('smart_filter', false);
    }
  };

  return (
    <div className="d-flex flex-column gap-3">
      <Row className="g-2">
        <Col xs={6}>
          <FilterOptionCard
            id="massive-smart-filter"
            variant="smart"
            label="فیلتر با AI"
            checked={values.smart_filter}
            active={values.smart_filter}
            tooltip={keywordResearchTooltips.massive.smartFilter}
            onChange={handleSmartFilterChange}
          />
        </Col>
        <Col xs={6}>
          <FilterOptionCard
            id="massive-filter-results"
            variant="results"
            label="فیلتر شخصی"
            checked={values.filter_results}
            active={values.filter_results}
            tooltip={keywordResearchTooltips.massive.filterResults}
            onChange={handleFilterResultsChange}
            badge={
              hasActiveFilters && values.filter_results ? (
                <span className="badge bg-primary fs--2">فعال</span>
              ) : undefined
            }
          />
        </Col>
      </Row>

      {values.filter_results && (
        <Row className="g-3">
          <Col xs={12} sm={6}>
            <MassiveKeywordLinesInput
              label="شامل"
              helpTooltip={keywordResearchTooltips.massive.include}
              name="include_keywords"
              values={values.include_keywords}
              setFieldValue={setFieldValue}
              handleBlur={handleBlur}
              touched={touched.include_keywords}
              error={
                touched.include_keywords
                  ? String(errors.include_keywords ?? '')
                  : undefined
              }
              rows={3}
              headerEnd={
                <OperatorToggle
                  value={values.include_operator}
                  onChange={v => setFieldValue('include_operator', v)}
                  disabled={includeCount < 2}
                />
              }
            />
          </Col>
          <Col xs={12} sm={6}>
            <MassiveKeywordLinesInput
              label="حذف"
              helpTooltip={keywordResearchTooltips.massive.exclude}
              name="exclude_keywords"
              values={values.exclude_keywords}
              setFieldValue={setFieldValue}
              handleBlur={handleBlur}
              touched={touched.exclude_keywords}
              error={
                touched.exclude_keywords
                  ? String(errors.exclude_keywords ?? '')
                  : undefined
              }
              rows={3}
              headerEnd={
                <OperatorToggle
                  value={values.exclude_operator}
                  onChange={v => setFieldValue('exclude_operator', v)}
                  disabled={excludeCount < 2}
                />
              }
            />
          </Col>
        </Row>
      )}
    </div>
  );
};

export default MassiveFormFiltersSection;
